import { useCallback, useState } from "react";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { useKillSwitch } from "../../hooks/api/admin/useKillSwitch";
import { SPACING } from "../../constants/design/spacing";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_STYLE } from "../../constants/design/fontSize";

// Formats a USD numeric string with thousands separators ("50000" → "$50,000").
const fmtUsd = (raw?: string): string => {
  if (!raw) return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

// KillSwitchControls is a one-row status bar that fits into the 50px top
// slot in WithdrawalContentWrapper's grid (grid-template-rows: 50px 1fr).
// Designed to be unobtrusive when OK and loud when TRIPPED, so ops can
// read state at a glance and take action (reset / retune threshold)
// without a detour. Polls GET /daily-kill-switch every 10s.
export function KillSwitchControls() {
  const {
    data,
    isLoading,
    resetDailyKillSwitch,
    setDailyKillSwitchThreshold,
  } = useKillSwitch();

  const [thresholdInput, setThresholdInput] = useState<string>("");

  const onReset = useCallback(async () => {
    const confirmed = window.confirm(
      "Reset the daily kill switch?\n\n" +
        "Zeroes the cumulative counter and clears the tripped flag. " +
        "Already-flagged withdrawals stay flagged and must still be " +
        "approved individually."
    );
    if (!confirmed) return;
    await resetDailyKillSwitch();
    // Reset only un-trips the global switch and zeroes the counter — it
    // does NOT un-flag the accounts that got caught while it was tripped.
    // Each affected account stays in manualMode=true with all its
    // pending/future withdrawals routed to manual review until an admin
    // explicitly toggles it off. Remind ops here so the queue doesn't
    // silently pile up.
    window.alert(
      "Kill switch reset.\n\n" +
        "Next steps:\n" +
        "1. Review pending withdrawals flagged with reason " +
        "DAILY_KILL_SWITCH_TRIPPED in the Withdrawals table.\n" +
        "2. For each affected account, toggle off Manual Mode after " +
        "reviewing — reset alone does NOT un-flag accounts."
    );
  }, [resetDailyKillSwitch]);

  const onSetThreshold = useCallback(async () => {
    const value = Number(thresholdInput);
    if (!Number.isFinite(value) || value <= 0) {
      window.alert("Threshold must be a positive number.");
      return;
    }
    const confirmed = window.confirm(
      `Set the daily kill-switch threshold to $${value.toLocaleString()}?\n\n` +
        "Lowering below current usage will trip the switch immediately. " +
        "Raising above it will un-trip."
    );
    if (!confirmed) return;
    await setDailyKillSwitchThreshold(value);
    setThresholdInput("");
  }, [setDailyKillSwitchThreshold, thresholdInput]);

  const tripped = data?.tripped;
  let statusLabel: string;
  if (isLoading || !data) {
    statusLabel = "…";
  } else if (tripped) {
    statusLabel = "TRIPPED";
  } else {
    statusLabel = "OK";
  }
  const statusColor = tripped ? COLORS.red.one : COLORS.green.one;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: SPACING.two,
        padding: `0 ${SPACING.three}px`,
        background: tripped
          ? `${COLORS.red.five}`
          : BACKGROUND_COLORS.componentSolid,
        borderBottom: `1px solid ${BORDER_COLORS.one}`,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <span
        style={{
          ...FONT_STYLE.label.two,
          color: TEXT_COLORS.two,
          whiteSpace: "nowrap",
        }}
      >
        Kill Switch
      </span>
      <span
        style={{
          ...FONT_STYLE.label.one,
          color: statusColor,
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {statusLabel}
      </span>
      <span
        style={{
          ...FONT_STYLE.label.two,
          color: TEXT_COLORS.two,
          whiteSpace: "nowrap",
        }}
      >
        {fmtUsd(data?.used)} / {fmtUsd(data?.threshold)}
      </span>

      <div style={{ flex: 1 }} />

      <input
        name="threshold"
        type="number"
        min="0"
        step="any"
        placeholder="New threshold (USD)"
        value={thresholdInput}
        onChange={(e) => setThresholdInput(e.target.value)}
        style={{
          width: 180,
          height: 28,
          padding: `0 ${SPACING.two}px`,
          borderRadius: 4,
          border: `1px solid ${BORDER_COLORS.one}`,
          background: BACKGROUND_COLORS.component,
          color: TEXT_COLORS.one,
        }}
      />
      <Button
        buttonTheme={ButtonThemeEnum.NEUTRAL2}
        disabled={!thresholdInput || isLoading}
        onClick={onSetThreshold}
        style={{ height: 28, padding: `0 ${SPACING.two}px` }}
      >
        Set Threshold
      </Button>
      <Button
        buttonTheme={ButtonThemeEnum.NEGATIVE}
        onClick={onReset}
        disabled={isLoading}
        style={{ height: 28, padding: `0 ${SPACING.two}px` }}
      >
        Reset
      </Button>
    </div>
  );
}
